/**
 * Save / load — docs/01-architecture.md §8.
 *
 * The load-bearing rule: **cell state is never serialised wholesale.** A world
 * is (seed + generator) plus a diff log of player-caused terrain changes, which
 * keeps a save in the tens of KB instead of the tens of MB a chunk dump would
 * cost. `applyDiffLog()` is the replay half of that contract.
 *
 * Everything here must survive a hostile disk: a corrupt save falls back to a
 * fresh profile rather than bricking the game, and a quota-exceeded write sheds
 * the largest droppable payload (the diff log, then the run) before giving up.
 *
 * The migration path is written now, with one version, deliberately. Retrofitting
 * versioning onto saves that are already on players' machines is not possible.
 */

import { normaliseFactionState } from './factions';
import { DEFAULT_KIT } from './kits';
import { Progression, createProgressionState } from './progression';
import type { ProgressionState } from './progression';
import type { RunState } from './runState';

export const SAVE_KEY = 'noitroidvania:save';
/**
 * The pre-rename storage key.
 *
 * The project was briefly called "Noitioidvania" and its save key carried that
 * spelling. Renaming the key without a migration would silently orphan every
 * existing profile — the game would boot to a fresh save and the player would
 * simply have lost their unlocks, with no error to explain it. `load()` falls
 * back to this key once and rewrites under the new one.
 */
export const LEGACY_SAVE_KEY = 'noitioidvania:save';
/**
 * v2 added `RunState.kit` and the in-run currency `inventory.scrip`. Both are
 * numbers/ids the run arithmetic reads directly, so a v1 run resumed without
 * them would spend `undefined` and produce `NaN` — the version bump and
 * `MIGRATIONS[1]` are what make that impossible rather than merely unlikely.
 *
 * v3 landed the four systems that had unlocks and no state to put them in
 * (issues #4, #5, #7, #8): `RunState.modifiers` and `inventory.materials`, and
 * on the profile `worldModifiers`, `stash`, `carriedAsh` and `mapEcho`. The run
 * half is the load-bearing part — `crafting.ts` writes into
 * `inventory.materials` by key, and writing a key into `undefined` throws where
 * `scrip` merely produced `NaN`. `normaliseState` would repair the profile half
 * on its own; the migration exists so that repair is not the only thing
 * standing between a v2 save and a crash on the first harvest.
 *
 * v4 wired the faction system to the world (issue #11) and gave it somewhere to
 * spend the currency. `RunState.vendors` is the load-bearing half, for the same
 * reason `inventory.materials` was: `markVendorPurchase` pushes onto
 * `run.vendors.purchased`, and pushing onto `undefined` throws where a missing
 * number merely produced `NaN`. `run.factions` is repaired here too — it has
 * existed since v1 but was always all zeroes because nothing wrote to it, so
 * this is the first version in which a malformed axis could actually matter.
 */
export const SAVE_VERSION = 5;

// ------------------------------------------------------------- diff log

/**
 * Terrain ops. Two are enough: a point write and a rect fill. Everything the
 * player does to terrain (a breach, an explosion crater, a sprayed reagent
 * volume) reduces to those, and both encode to a fixed 7-number stride so the
 * whole log serialises as one flat numeric array.
 */
export const DiffOp = {
  /** Single cell -> mat. mat 0 is the empty/air material by convention. */
  SET: 0,
  /** w×h rect from (x,y) -> mat. */
  RECT: 1,
} as const;

export type DiffOpId = (typeof DiffOp)[keyof typeof DiffOp];

export interface TerrainDiffEntry {
  tick: number;
  op: DiffOpId;
  x: number;
  y: number;
  w: number;
  h: number;
  mat: number;
}

export interface TerrainDiffLog {
  /** The seed the diffs are relative to. Replaying against another seed is a bug. */
  seed: number;
  entries: TerrainDiffEntry[];
}

/** Structural sink so this module never imports world/. */
export interface TerrainSink {
  setCell(x: number, y: number, mat: number): void;
}

/** Auto-compact above this many entries, to keep saves inside quota. */
export const DIFF_COMPACT_THRESHOLD = 20000;

const DIFF_STRIDE = 7;

export function createDiffLog(seed: number): TerrainDiffLog {
  return { seed, entries: [] };
}

export function recordCell(log: TerrainDiffLog, tick: number, x: number, y: number, mat: number): void {
  log.entries.push({ tick, op: DiffOp.SET, x, y, w: 1, h: 1, mat });
  if (log.entries.length > DIFF_COMPACT_THRESHOLD) compactDiffLog(log);
}

export function recordRect(
  log: TerrainDiffLog,
  tick: number,
  x: number,
  y: number,
  w: number,
  h: number,
  mat: number,
): void {
  log.entries.push({ tick, op: DiffOp.RECT, x, y, w, h, mat });
  if (log.entries.length > DIFF_COMPACT_THRESHOLD) compactDiffLog(log);
}

/**
 * Replay. The caller has already regenerated chunks from `log.seed`; this lays
 * the player's history back on top, in order, last-write-wins per cell.
 */
export function applyDiffLog(log: TerrainDiffLog, sink: TerrainSink, upToTick = Infinity): number {
  let applied = 0;
  for (const e of log.entries) {
    if (e.tick > upToTick) break;
    if (e.op === DiffOp.SET) {
      sink.setCell(e.x, e.y, e.mat);
      applied++;
    } else {
      for (let dy = 0; dy < e.h; dy++) {
        for (let dx = 0; dx < e.w; dx++) {
          sink.setCell(e.x + dx, e.y + dy, e.mat);
          applied++;
        }
      }
    }
  }
  return applied;
}

function covers(e: TerrainDiffEntry, x: number, y: number): boolean {
  return x >= e.x && x < e.x + e.w && y >= e.y && y < e.y + e.h;
}

/**
 * Drop entries that a later entry fully overwrites. Because apply is
 * last-write-wins per cell, removing a fully-shadowed write is provably
 * replay-equivalent — the compacted log produces byte-identical terrain.
 *
 * Conservative on rects: a rect is dropped only if a single later rect fully
 * contains it. Partial overlaps are cheap enough to keep.
 */
export function compactDiffLog(log: TerrainDiffLog): TerrainDiffLog {
  const src = log.entries;
  const kept: TerrainDiffEntry[] = [];
  const laterSets = new Set<number>();
  const laterRects: TerrainDiffEntry[] = [];

  for (let i = src.length - 1; i >= 0; i--) {
    const e = src[i];
    let shadowed = false;
    if (e.op === DiffOp.SET) {
      shadowed = laterSets.has(cellKey(e.x, e.y));
      if (!shadowed) {
        for (const r of laterRects) {
          if (covers(r, e.x, e.y)) {
            shadowed = true;
            break;
          }
        }
      }
      if (!shadowed) laterSets.add(cellKey(e.x, e.y));
    } else {
      for (const r of laterRects) {
        if (
          e.x >= r.x &&
          e.y >= r.y &&
          e.x + e.w <= r.x + r.w &&
          e.y + e.h <= r.y + r.h
        ) {
          shadowed = true;
          break;
        }
      }
      if (!shadowed) laterRects.push(e);
    }
    if (!shadowed) kept.push(e);
  }

  kept.reverse();
  log.entries = kept;
  return log;
}

/** Pack x,y into one number for Set membership. Worlds fit in ±2^15 per axis. */
function cellKey(x: number, y: number): number {
  return ((x + 32768) << 16) | ((y + 32768) & 0xffff);
}

export function encodeDiffLog(log: TerrainDiffLog): number[] {
  const out = new Array<number>(log.entries.length * DIFF_STRIDE);
  let i = 0;
  for (const e of log.entries) {
    out[i++] = e.tick;
    out[i++] = e.op;
    out[i++] = e.x;
    out[i++] = e.y;
    out[i++] = e.w;
    out[i++] = e.h;
    out[i++] = e.mat;
  }
  return out;
}

export function decodeDiffLog(seed: number, flat: readonly number[]): TerrainDiffLog {
  const entries: TerrainDiffEntry[] = [];
  for (let i = 0; i + DIFF_STRIDE <= flat.length; i += DIFF_STRIDE) {
    entries.push({
      tick: flat[i],
      op: (flat[i + 1] === DiffOp.RECT ? DiffOp.RECT : DiffOp.SET) as DiffOpId,
      x: flat[i + 2],
      y: flat[i + 3],
      w: flat[i + 4],
      h: flat[i + 5],
      mat: flat[i + 6],
    });
  }
  return { seed, entries };
}

// --------------------------------------------------------------- envelope

export interface ProfileData {
  createdAtMs: number;
  playtimeMs: number;
  progression: ProgressionState;
  settings: Record<string, string | number | boolean>;
  /**
   * The surface's dressing clock — `docs/27` §3.1's middle row. The authored
   * frame is pure in nothing and never resets; everything below `y = 0` is
   * pure in the run seed and resets every run; the surface's *dressing* and
   * the player's marks on it are keyed to this, advanced by the profile on a
   * schedule the profile owns (a milestone, a story beat) — never per run.
   * Constant until something earns the right to advance it: the machinery is
   * what this field buys, not the schedule.
   */
  surfaceSeed: number;
  /**
   * The profile's marks on the surface — `SurfaceTerrain`'s log, exactly
   * `RunState.terrain` one owner up. Keyed on `surfaceSeed`, not on any run
   * seed: the frame above `y = 0` is seed-invariant, so a hole dug there is
   * the same hole in every run until the dressing clock advances.
   */
  surface: TerrainDiffLog;
}

/** RunState with the non-JSON parts flattened. */
export interface SerializedRun extends Omit<RunState, 'rooms' | 'terrain'> {
  rooms: { current: string; visited: string[]; edges: string[] };
  terrain: { seed: number; entries: number[] };
}

export interface SaveEnvelope {
  v: number;
  savedAtMs: number;
  profile: SerializedProfile;
  run: SerializedRun | null;
}

export function createProfile(nowMs = Date.now()): ProfileData {
  return {
    createdAtMs: nowMs,
    playtimeMs: 0,
    progression: createProgressionState(),
    settings: {},
    // 1, not `nowMs`: the dressing clock is authored state, and a fresh
    // profile's surface must be the same surface in every test and every
    // install. Advancing it is a decision, not an accident of creation time.
    surfaceSeed: 1,
    surface: createDiffLog(1),
  };
}

/** ProfileData with the surface log flattened, `SerializedRun`'s shape. */
export interface SerializedProfile extends Omit<ProfileData, 'surface'> {
  surface: { seed: number; entries: number[] };
}

export function serializeProfile(p: ProfileData): SerializedProfile {
  const { surface, ...rest } = p;
  return { ...rest, surface: { seed: surface.seed, entries: encodeDiffLog(surface) } };
}

export function serializeRun(run: RunState): SerializedRun {
  const { rooms, terrain, ...rest } = run;
  return {
    ...rest,
    rooms: { current: rooms.current, visited: [...rooms.visited], edges: [...rooms.edges] },
    terrain: { seed: terrain.seed, entries: encodeDiffLog(terrain) },
  };
}

export function deserializeRun(s: SerializedRun): RunState {
  const { rooms, terrain, ...rest } = s;
  return {
    ...rest,
    // Belt-and-braces over `MIGRATIONS[2]`, in the same spirit as the `rooms`
    // and `terrain` defaults below: the migration is what guarantees these for a
    // v2 save, and this is what guarantees them for a payload that reached here
    // some other way. `crafting.ts` writes into `materials` by key, and that is
    // the one field whose absence throws rather than degrades.
    modifiers: Array.isArray(rest.modifiers) ? [...rest.modifiers] : [],
    inventory: {
      ...rest.inventory,
      materials: { ...(rest.inventory?.materials ?? {}) },
    },
    // Same belt-and-braces, for the same two reasons: `vendors.purchased` is
    // pushed onto, and a broken `factions` blob reads as permanent neutrality
    // rather than as an error (issue #11).
    vendors: { purchased: [...(rest.vendors?.purchased ?? [])] },
    factions: normaliseFactionState(rest.factions),
    rooms: {
      current: rooms?.current ?? '',
      visited: new Set(rooms?.visited ?? []),
      edges: [...(rooms?.edges ?? [])],
    },
    terrain: decodeDiffLog(terrain?.seed ?? rest.seed, terrain?.entries ?? []),
    // Same belt-and-braces, issue #172: a save from before this field existed
    // has no `revival` at all, and reading `.chargesRemaining` off `undefined`
    // at the next lethal hit would throw rather than just degrade — a resumed
    // pre-#172 save simply has no reassemble charge until its next room.
    revival: { chargesRemaining: rest.revival?.chargesRemaining ?? 0, lastRoomId: rest.revival?.lastRoomId ?? null },
  } as RunState;
}

// -------------------------------------------------------------- migration

export type Migration = (raw: any) => any;

/**
 * `MIGRATIONS[n]` upgrades a v-n envelope to v-(n+1).
 *
 * A migration reads a shape that no longer has a type, so it is written against
 * `any` and must never throw: a save that fails to migrate is quarantined and
 * the player loses the profile. Defaulting is always the right answer here.
 */
export const MIGRATIONS: Record<number, Migration> = {
  1: (raw) => {
    const run = raw?.run
      ? {
          ...raw.run,
          kit: typeof raw.run.kit === 'string' && raw.run.kit ? raw.run.kit : DEFAULT_KIT,
          inventory: {
            ...(raw.run.inventory ?? {}),
            ash: Number(raw.run.inventory?.ash) || 0,
            // A v1 run predates the currency, so it earned none. Zero is both
            // the honest value and the one the run would have had at its start.
            scrip: 0,
          },
        }
      : null;
    const progression = raw?.profile?.progression
      ? { ...raw.profile.progression, startKit: raw.profile.progression.startKit ?? DEFAULT_KIT }
      : raw?.profile?.progression;
    return {
      ...raw,
      v: 2,
      profile: raw?.profile ? { ...raw.profile, progression } : raw?.profile,
      run,
    };
  },
  2: (raw) => {
    const run = raw?.run
      ? {
          ...raw.run,
          // A v2 run was generated with no modifiers, and saying so explicitly
          // matters: an empty array means "a plain world", which is exactly what
          // it was, and lets `resolveWorldModifiers` stay total.
          modifiers: Array.isArray(raw.run.modifiers) ? raw.run.modifiers : [],
          inventory: {
            ...(raw.run.inventory ?? {}),
            // The pouch must be an object, not merely present: `crafting.ts`
            // writes `materials[name] = n`, which throws on undefined rather
            // than degrading to NaN the way a missing number would.
            materials:
              raw.run.inventory?.materials && typeof raw.run.inventory.materials === 'object'
                ? raw.run.inventory.materials
                : {},
          },
        }
      : null;
    const p = raw?.profile?.progression;
    const progression = p
      ? {
          ...p,
          worldModifiers: Array.isArray(p.worldModifiers) ? p.worldModifiers : [],
          stash: Array.isArray(p.stash) ? p.stash : [],
          carriedAsh: Number(p.carriedAsh) || 0,
          // A v2 profile has no echo, and the passive that writes one had no
          // consumer, so null is the honest value rather than a default.
          mapEcho: p.mapEcho ?? null,
        }
      : p;
    return {
      ...raw,
      v: 3,
      profile: raw?.profile ? { ...raw.profile, progression } : raw?.profile,
      run,
    };
  },
  3: (raw) => {
    const run = raw?.run
      ? {
          ...raw.run,
          // The ledger must be an object with an array, not merely present:
          // `markVendorPurchase` pushes onto it. Same failure mode as the
          // material pouch in MIGRATIONS[2], and the same answer.
          vendors: {
            purchased: Array.isArray(raw.run.vendors?.purchased)
              ? raw.run.vendors.purchased.filter((s: unknown) => typeof s === 'string')
              : [],
          },
          // A v3 run's faction blob is structurally fine but was never written
          // to, so this is a no-op in practice. It runs anyway because from v4
          // on the axes carry real play, and `normaliseFactionState` is the
          // only thing standing between a hand-edited save and a reputation
          // that reads NaN — which clamps to nothing and compares false against
          // every band, i.e. permanent silent neutrality.
          factions: normaliseFactionState(raw.run.factions),
        }
      : null;
    return { ...raw, v: 4, run };
  },
  4: (raw) => {
    // docs/27 §3.1: the profile gains the surface's dressing clock and its
    // marks on the surface. A v4 profile has dug nothing the surface log can
    // know about — its surface digs live in whatever run log recorded them —
    // so the new log starts empty rather than guessing.
    const profile = raw?.profile
      ? {
          ...raw.profile,
          surfaceSeed: 1,
          surface: { seed: 1, entries: [] },
        }
      : raw?.profile;
    return { ...raw, v: 5, profile };
  },
};

export function migrate(raw: any): { data: any; from: number; migrated: boolean } {
  let v = typeof raw?.v === 'number' ? raw.v : 0;
  const from = v;
  let data = raw;
  // v0 = pre-versioning. Nothing shipped there, so treat it as v1-shaped.
  if (v === 0) {
    data = { ...raw, v: 1 };
    v = 1;
  }
  while (v < SAVE_VERSION) {
    const step = MIGRATIONS[v];
    if (!step) throw new Error(`no migration from save v${v}`);
    data = step(data);
    v = typeof data?.v === 'number' ? data.v : v + 1;
  }
  return { data, from, migrated: from !== SAVE_VERSION };
}

// ---------------------------------------------------------------- storage

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/**
 * Resolve localStorage, or null. Probing with a real write matters: Safari
 * private mode exposes the API and then throws on every setItem, so mere
 * presence is not availability.
 */
export function detectStorage(): StorageLike | null {
  try {
    const ls = (globalThis as { localStorage?: StorageLike }).localStorage;
    if (!ls) return null;
    const probe = `${SAVE_KEY}:probe`;
    ls.setItem(probe, '1');
    ls.removeItem(probe);
    return ls;
  } catch {
    return null;
  }
}

function isQuotaError(e: unknown): boolean {
  if (!e || typeof e !== 'object') return false;
  const err = e as { name?: string; code?: number };
  return (
    err.name === 'QuotaExceededError' ||
    err.name === 'NS_ERROR_DOM_QUOTA_REACHED' ||
    err.code === 22 ||
    err.code === 1014
  );
}

// ------------------------------------------------------------------ result

export type LoadStatus =
  | 'ok'
  | 'fresh'
  /** Read and upgraded from an older schema. */
  | 'migrated'
  /** The stored payload was unusable; a fresh profile was substituted. */
  | 'recovered'
  /** No storage at all — the session will not persist. */
  | 'unavailable';

export interface LoadResult {
  status: LoadStatus;
  profile: ProfileData;
  progression: Progression;
  run: RunState | null;
  fromVersion: number | null;
  error: string | null;
}

export type SaveError = 'none' | 'unavailable' | 'quota' | 'serialize';

export interface SaveResult {
  ok: boolean;
  error: SaveError;
  bytes: number;
  /** True when the run had to be dropped to fit inside quota. */
  shedRun: boolean;
  message: string | null;
}

// ------------------------------------------------------------- save system

export interface SaveSystemOptions {
  storage?: StorageLike | null;
  key?: string;
  now?: () => number;
}

export class SaveSystem {
  readonly key: string;
  readonly storage: StorageLike | null;
  private now: () => number;

  constructor(opts: SaveSystemOptions = {}) {
    this.key = opts.key ?? SAVE_KEY;
    this.storage = opts.storage === undefined ? detectStorage() : opts.storage;
    this.now = opts.now ?? (() => Date.now());
  }

  get available(): boolean {
    return this.storage !== null;
  }

  load(): LoadResult {
    const fresh = (status: LoadStatus, error: string | null): LoadResult => {
      const profile = createProfile(this.now());
      return {
        status,
        profile,
        progression: new Progression(profile.progression),
        run: null,
        fromVersion: null,
        error,
      };
    };

    if (!this.storage) return fresh('unavailable', 'localStorage is not available');

    let raw: string | null;
    try {
      raw = this.storage.getItem(this.key);
      // One-time adoption of a profile written under the pre-rename key.
      if (raw === null && this.key === SAVE_KEY) {
        raw = this.storage.getItem(LEGACY_SAVE_KEY);
        if (raw !== null) this.storage.setItem(SAVE_KEY, raw);
      }
    } catch (e) {
      return fresh('recovered', `read failed: ${String(e)}`);
    }
    if (raw === null) return fresh('fresh', null);

    let parsed: any;
    try {
      parsed = JSON.parse(raw);
    } catch (e) {
      // Quarantine rather than delete — a corrupt save is the only evidence of
      // whatever corrupted it, and the player loses nothing they still had.
      this.quarantine(raw);
      return fresh('recovered', `corrupt JSON: ${String(e)}`);
    }

    if (!parsed || typeof parsed !== 'object') {
      this.quarantine(raw);
      return fresh('recovered', 'save payload is not an object');
    }
    if (typeof parsed.v === 'number' && parsed.v > SAVE_VERSION) {
      // A newer client wrote this. Refuse to downgrade, keep the file intact.
      return fresh('recovered', `save is from a newer version (v${parsed.v})`);
    }

    let data: any;
    let from: number;
    let migrated: boolean;
    try {
      const r = migrate(parsed);
      data = r.data;
      from = r.from;
      migrated = r.migrated;
    } catch (e) {
      this.quarantine(raw);
      return fresh('recovered', `migration failed: ${String(e)}`);
    }

    try {
      const profile = hydrateProfile(data.profile, this.now());
      const progression = new Progression(profile.progression);
      // Progression's constructor normalises; keep the profile pointing at the
      // repaired object so a save-after-load writes the clean shape.
      profile.progression = progression.state;
      const run = data.run ? deserializeRun(data.run as SerializedRun) : null;
      return {
        status: migrated ? 'migrated' : 'ok',
        profile,
        progression,
        run,
        fromVersion: from,
        error: null,
      };
    } catch (e) {
      this.quarantine(raw);
      return fresh('recovered', `hydrate failed: ${String(e)}`);
    }
  }

  save(profile: ProfileData, run: RunState | null): SaveResult {
    if (!this.storage) {
      return { ok: false, error: 'unavailable', bytes: 0, shedRun: false, message: 'no storage' };
    }

    let envelope: SaveEnvelope;
    try {
      envelope = {
        v: SAVE_VERSION,
        savedAtMs: this.now(),
        profile: serializeProfile(profile),
        run: run ? serializeRun(run) : null,
      };
    } catch (e) {
      return { ok: false, error: 'serialize', bytes: 0, shedRun: false, message: String(e) };
    }

    const first = this.write(envelope);
    if (first.ok || first.error !== 'quota') return first;

    // Quota shedding, cheapest loss first: compact the diff log, then drop the
    // run entirely. Meta progression is the thing that must never be lost.
    if (run) {
      compactDiffLog(run.terrain);
      const compacted = this.write({ ...envelope, run: serializeRun(run) });
      if (compacted.ok) return compacted;
    }
    const withoutRun = this.write({ ...envelope, run: null });
    if (withoutRun.ok) {
      return { ...withoutRun, shedRun: true, message: 'run dropped to fit storage quota' };
    }
    return withoutRun;
  }

  clear(): void {
    try {
      this.storage?.removeItem(this.key);
    } catch {
      /* nothing useful to do */
    }
  }

  /** Read back the raw string. Diagnostics and tests only. */
  peek(): string | null {
    try {
      return this.storage?.getItem(this.key) ?? null;
    } catch {
      return null;
    }
  }

  private write(envelope: SaveEnvelope): SaveResult {
    let json: string;
    try {
      json = JSON.stringify(envelope);
    } catch (e) {
      return { ok: false, error: 'serialize', bytes: 0, shedRun: false, message: String(e) };
    }
    try {
      this.storage!.setItem(this.key, json);
      return { ok: true, error: 'none', bytes: json.length, shedRun: false, message: null };
    } catch (e) {
      return {
        ok: false,
        error: isQuotaError(e) ? 'quota' : 'unavailable',
        bytes: json.length,
        shedRun: false,
        message: String(e),
      };
    }
  }

  private quarantine(raw: string): void {
    try {
      this.storage?.setItem(`${this.key}:corrupt`, raw);
      this.storage?.removeItem(this.key);
    } catch {
      // If even the quarantine write fails we still return a fresh profile —
      // never let save recovery be the thing that throws.
      try {
        this.storage?.removeItem(this.key);
      } catch {
        /* give up quietly */
      }
    }
  }
}

function hydrateProfile(raw: any, nowMs: number): ProfileData {
  const base = createProfile(nowMs);
  if (!raw || typeof raw !== 'object') return base;
  const surfaceSeed = typeof raw.surfaceSeed === 'number' ? raw.surfaceSeed : base.surfaceSeed;
  return {
    createdAtMs: typeof raw.createdAtMs === 'number' ? raw.createdAtMs : base.createdAtMs,
    playtimeMs: typeof raw.playtimeMs === 'number' ? raw.playtimeMs : 0,
    progression: raw.progression ?? base.progression,
    settings: raw.settings && typeof raw.settings === 'object' ? { ...raw.settings } : {},
    surfaceSeed,
    // The same belt-and-braces `deserializeRun` wears for `run.terrain`: the
    // v4→v5 migration guarantees the field for a migrated save, and this
    // guarantees it for a payload that reached here some other way. A log
    // whose seed disagrees with the profile's clock is dropped at bind by
    // `SurfaceTerrain`'s guard, not here — hydration repairs shape, not truth.
    surface: decodeDiffLog(raw.surface?.seed ?? surfaceSeed, raw.surface?.entries ?? []),
  };
}

// ------------------------------------------------------------- autosave

export interface AutosaveOptions {
  /** Coalesce bursts of requests. */
  debounceMs?: number;
  /** Hard ceiling on write frequency, regardless of debounce. */
  minIntervalMs?: number;
  now?: () => number;
  schedule?: (fn: () => void, ms: number) => unknown;
  cancel?: (handle: unknown) => void;
}

/**
 * Debounced autosave. Writes are ~1 ms of JSON.stringify against a 16.6 ms
 * budget, so the point of debouncing is not cost but churn: a room transition
 * fires half a dozen "state changed" signals and they should be one write.
 */
export class Autosaver {
  private handle: unknown = null;
  private lastWriteMs = -Infinity;
  private dirty = false;
  private disposed = false;

  readonly debounceMs: number;
  readonly minIntervalMs: number;
  private now: () => number;
  private schedule: (fn: () => void, ms: number) => unknown;
  private cancelFn: (handle: unknown) => void;

  constructor(
    private readonly writeFn: () => SaveResult,
    opts: AutosaveOptions = {},
  ) {
    this.debounceMs = opts.debounceMs ?? 1500;
    this.minIntervalMs = opts.minIntervalMs ?? 5000;
    this.now = opts.now ?? (() => Date.now());
    this.schedule =
      opts.schedule ?? ((fn, ms) => setTimeout(fn, ms) as unknown as ReturnType<typeof setTimeout>);
    this.cancelFn = opts.cancel ?? ((h) => clearTimeout(h as ReturnType<typeof setTimeout>));
  }

  /** Mark dirty; the write lands after the debounce and rate limit allow it. */
  request(): void {
    if (this.disposed) return;
    this.dirty = true;
    if (this.handle !== null) return;
    const sinceLast = this.now() - this.lastWriteMs;
    const wait = Math.max(this.debounceMs, this.minIntervalMs - sinceLast);
    this.handle = this.schedule(() => {
      this.handle = null;
      this.flush();
    }, Math.max(0, wait));
  }

  /** Write now if dirty. Call on run end, pause, and pagehide. */
  flush(): SaveResult | null {
    if (this.disposed || !this.dirty) return null;
    this.dirty = false;
    this.lastWriteMs = this.now();
    if (this.handle !== null) {
      this.cancelFn(this.handle);
      this.handle = null;
    }
    return this.writeFn();
  }

  get pending(): boolean {
    return this.dirty;
  }

  dispose(): void {
    if (this.handle !== null) this.cancelFn(this.handle);
    this.handle = null;
    this.disposed = true;
  }
}

/**
 * Wire an autosaver to the browser lifecycle. `pagehide` is the only reliable
 * last-chance hook in Chrome — `beforeunload` is not fired on mobile tab kill.
 * Returns a disposer.
 *
 * **Uncalled, and #118 judged it a broken feature rather than dead code.** The
 * bug is real and is the one this function exists to fix: nothing writes the
 * run except a death, a stash withdrawal, a craft, and `main.ts`'s `stopGame`,
 * so a player who closes the tab mid-run loses it — and closing the tab is how
 * a browser game ends. `main.ts` says so itself, at the one write it added as a
 * stopgap.
 *
 * It cannot be closed here, and not because of file ownership. `flush()` is a
 * no-op unless something has called `request()`, and the only code that knows
 * the run changed is the run loop. So the hooks and the `Autosaver` are one
 * fix with two halves and the second half is a `core/game.ts` hunk; wiring this
 * alone would attach two listeners to a saver that is never dirty and produce
 * exactly the appearance of a fix. Gated in `tests/liveness.test.ts` under
 * `PENDING_GAME_HUNKS`, which goes red the moment the other half lands.
 */
export function attachAutosaveHooks(auto: Autosaver): () => void {
  const target = (globalThis as { addEventListener?: typeof addEventListener; removeEventListener?: typeof removeEventListener });
  if (!target.addEventListener) return () => auto.dispose();
  const onHide = () => auto.flush();
  const onVisibility = () => {
    const d = (globalThis as { document?: { visibilityState?: string } }).document;
    if (d?.visibilityState === 'hidden') auto.flush();
  };
  target.addEventListener('pagehide', onHide);
  target.addEventListener('visibilitychange', onVisibility);
  return () => {
    target.removeEventListener?.('pagehide', onHide);
    target.removeEventListener?.('visibilitychange', onVisibility);
    auto.dispose();
  };
}
